import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { CalculateIcon } from '@hugeicons-pro/core-bulk-rounded'
import { AlertCircleIcon, CancelCircleIcon } from '@hugeicons-pro/core-duotone-rounded'
import { ArrowRight02Icon } from '@hugeicons-pro/core-twotone-rounded'
import { Box, Button, Divider, Link, Typography } from '@mui/material'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'

import { useGetOrderItemsQuery, useUpdateOrderItemsAttributesMutation } from '@/app/services/orderItems'
import { useCalculateOrderMutation, useGetOrderQuery, useUpdateOrderMutation } from '@/app/services/orders'
import { useGetStoreSettingsQuery } from '@/app/services/organisation'
import { selectedOrderStatus, selectIsUpdateQueueEmpty, setSelectedOrderStatus } from '@/app/slices/orderItemsSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { TbxTooltip } from '@/common/components'
import SecondaryProcessRows from '@/common/components/SecondaryProcessRows/SecondaryProcessRows'
import { useCurrencyFormatter } from '@/common/hooks/useCurrencyFormatter'
import { useMutationErrorSnackbar } from '@/common/hooks/useMutationErrorSnackbar'
import { ORDER_STATUS_ARRAY } from '@/common/utils'
import { orderHasIssues, orderHasUnsetItems, orderIssuesCount } from '@/common/utils/OrderUtils/OrderUtilities'

import OrderItemStatus from '../common/OrderItemStatus'

const classes = {
    orderSummary: {
        padding: 3,
        gap: 3,
        minHeight: {
            lg: 'calc(100vh - 114px)',
        },
    },
    summaryTitle: {
        textTransform: 'uppercase',
    },
    issueMessages: {
        gap: 3,
    },
    pricingSummary: {
        gap: 1.5,
    },
    checkoutButton: {
        gap: 1,
        '& a': {
            textDecoration: 'none',
        },
    },
    saveOrder: {
        gap: 1,
    },
}

const OrderSummary = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { showMutationError } = useMutationErrorSnackbar()

    const { orderId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const orderStatus = useSelector(selectedOrderStatus)
    const isUpdateQueueEmpty = useSelector(selectIsUpdateQueueEmpty)

    const { isFetching, isLoading: isLoadingOrder } = useGetOrderQuery({
        organisationId,
        orderId,
    })
    const { data: storeSettings } = useGetStoreSettingsQuery({
        organisationId,
    })

    const { data: order } = useGetOrderQuery({
        organisationId,
        orderId,
    })

    const { data: orderItems } = useGetOrderItemsQuery({
        organisationId,
        orderId,
    })

    const [calculateOrder, { isLoading: isCalculating }] = useCalculateOrderMutation({
        fixedCacheKey: 'shared-calculate-order',
    })
    const [, { isLoading: isUpdating }] = useUpdateOrderItemsAttributesMutation({
        fixedCacheKey: 'shared-update-order-items-attributes',
    })

    const [updateOrder] = useUpdateOrderMutation({
        fixedCacheKey: 'shared-update-order',
    })

    const hasIssues = orderHasIssues(orderItems)
    const hasUnsetItems = orderHasUnsetItems(orderItems)
    const issuesCount = orderIssuesCount(orderItems)

    const { formatCurrency } = useCurrencyFormatter({
        currencyCode: storeSettings?.currencyCode,
        locale: storeSettings?.locale,
    })

    const allItemsCalculated = useMemo(() => {
        if (!orderItems?.length) return false
        return (
            orderStatus === ORDER_STATUS_ARRAY.Calculated &&
            orderItems?.every((item) => Boolean(item?.itemPrice) && Boolean(item?.linePrice))
        )
    }, [orderStatus, orderItems])

    const prices = useMemo(() => {
        return {
            parts: order?.processingTotalPrice + order?.materialPrice,
            secondaryProcesses: order?.foldingTotalPrice + order?.secondaryProcessTotalPrice,
            miscellaneous: order?.miscellaneousItemsTotalPrice,
            adjustments: order?.otherChargeTotalPrice,
            minimumChargePrice: order?.minimumQuoteChargePrice,
            subtotal: order?.linePrice + (order?.minimumQuoteChargePrice || 0) - (order?.deliveryPrice || 0),
        }
    }, [order])

    const handleCalculateOrder = async () => {
        try {
            const response = await calculateOrder({
                organisationId,
                orderId,
            }).unwrap()

            dispatch(setSelectedOrderStatus(response?.status))
        } catch (error) {
            console.error('Error calculating order:', error)
            showMutationError(error, error?.data || t('There was an error calculating your order. Please try again.'))
        }
    }

    const handleOrderStatusChange = useCallback(
        async (order, newOrderStatus) => {
            try {
                await updateOrder({
                    organisationId,
                    orderId,
                    order: {
                        ...order,
                        status: newOrderStatus,
                    },
                }).unwrap()
                dispatch(setSelectedOrderStatus(newOrderStatus))
            } catch (error) {
                showMutationError(error, t('Please try again later.'))
            }
        },
        [dispatch, showMutationError, orderId, t, organisationId, updateOrder]
    )

    const navigateToReviewOrder = useCallback(async () => {
        handleOrderStatusChange(order, ORDER_STATUS_ARRAY.Draft)

        navigate(`/orders/${orderId}/review`)
    }, [handleOrderStatusChange, navigate, orderId, order])

    return (
        <Box
            sx={[
                {
                    display: 'flex',
                    flexDirection: 'column',
                },
                classes.orderSummary,
            ]}
        >
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    sx={classes.summaryTitle}
                    variant="strong1"
                >
                    {t('Order summary')}
                </Typography>

                <Box
                    style={{ gap: '6px' }}
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                    }}
                >
                    {!isLoadingOrder && hasIssues && Boolean(issuesCount.errors) ? (
                        <OrderItemStatus
                            count={issuesCount.errors}
                            status="error"
                        />
                    ) : null}
                    {!isLoadingOrder && hasIssues && Boolean(issuesCount.warnings) ? (
                        <OrderItemStatus
                            count={issuesCount.warnings}
                            status="warning"
                        />
                    ) : null}
                    {!isLoadingOrder && Boolean(issuesCount.success) ? (
                        <OrderItemStatus
                            count={issuesCount.success}
                            status="success"
                        />
                    ) : null}
                    {!isLoadingOrder && hasUnsetItems && Boolean(issuesCount.unset) ? (
                        <OrderItemStatus
                            count={issuesCount.unset}
                            status="unset"
                        />
                    ) : null}
                </Box>
            </Box>
            {!isLoadingOrder && (!order || hasIssues) ? (
                <Box
                    sx={[
                        {
                            display: 'flex',
                            flexDirection: 'column',
                        },
                        classes.issueMessages,
                    ]}
                >
                    {issuesCount.errors ? (
                        <Alert
                            icon={
                                <HugeiconsIcon
                                    icon={CancelCircleIcon}
                                    size={20}
                                />
                            }
                            severity="error"
                            variant="filled"
                        >
                            <AlertTitle>{t('Error')}</AlertTitle>
                            {t(
                                'There is a part in the order with an error. Make sure a material has been assigned to it. If the part has an issue, please delete this part to continue with your order or contact us for help.'
                            )}
                        </Alert>
                    ) : null}

                    {issuesCount.warnings ? (
                        <Alert
                            icon={
                                <HugeiconsIcon
                                    icon={AlertCircleIcon}
                                    size={20}
                                />
                            }
                            severity="warning"
                            variant="outlined"
                        >
                            <AlertTitle>{t('Warning')}</AlertTitle>
                            {t(
                                "A part in this order has issues that don't prevent calculation but might result in a higher price being calculated. Consider fixing the issues and reuploading the part to get a more accurate price."
                            )}
                        </Alert>
                    ) : null}

                    {!order ? (
                        <Alert
                            icon={
                                <HugeiconsIcon
                                    icon={AlertCircleIcon}
                                    size={20}
                                />
                            }
                            severity="error"
                            variant="filled"
                        >
                            <AlertTitle>{t('Error')}</AlertTitle>
                            {t('This order link is inactive. Please create a new order.')}
                        </Alert>
                    ) : null}
                </Box>
            ) : null}
            {Boolean(orderItems?.length) && !issuesCount.errors ? (
                <Box
                    sx={[
                        {
                            display: 'flex',
                            flexDirection: 'column',
                        },
                        classes.pricingSummary,
                    ]}
                >
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="body2">{t('Parts')}</Typography>
                        <Typography
                            className="price"
                            variant="strong2"
                        >
                            {allItemsCalculated && !isCalculating
                                ? formatCurrency(prices.parts)
                                : String.fromCharCode(8212)}
                        </Typography>
                    </Box>
                    {prices.secondaryProcesses ? (
                        <>
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography variant="body2">{t('Secondary processes')}</Typography>
                                <Typography
                                    className="secondaryProcesses"
                                    variant="strong2"
                                >
                                    {allItemsCalculated && !isCalculating
                                        ? formatCurrency(prices.secondaryProcesses)
                                        : String.fromCharCode(8212)}
                                </Typography>
                            </Box>
                            {allItemsCalculated && !isCalculating ? (
                                <SecondaryProcessRows
                                    currencyCode={storeSettings?.currencyCode}
                                    locale={storeSettings?.locale}
                                    order={order}
                                    orderItems={orderItems}
                                />
                            ) : null}
                        </>
                    ) : null}
                    {prices.miscellaneous ? (
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography variant="body2">{t('Miscellaneous Items')}</Typography>
                            <Typography
                                className="miscellaneous"
                                variant="strong2"
                            >
                                {allItemsCalculated && !isCalculating
                                    ? formatCurrency(prices.miscellaneous)
                                    : String.fromCharCode(8212)}
                            </Typography>
                        </Box>
                    ) : null}
                    {prices.adjustments ? (
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography variant="body2">{t('Adjustments')}</Typography>
                            <Typography
                                className="adjustments"
                                variant="strong2"
                            >
                                {allItemsCalculated && !isCalculating
                                    ? formatCurrency(prices.adjustments)
                                    : String.fromCharCode(8212)}
                            </Typography>
                        </Box>
                    ) : null}
                    {prices.minimumChargePrice ? (
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography variant="body2">{t('Minimum order charge')}</Typography>
                            <Typography
                                className="price"
                                variant="strong2"
                            >
                                {allItemsCalculated && !isCalculating
                                    ? formatCurrency(prices.minimumChargePrice)
                                    : String.fromCharCode(8212)}
                            </Typography>
                        </Box>
                    ) : null}
                    <Divider />
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="body1">{t('Subtotal')}</Typography>
                        <Typography
                            className="price"
                            variant="strong1"
                        >
                            {allItemsCalculated && !isCalculating
                                ? formatCurrency(prices.subtotal)
                                : String.fromCharCode(8212)}
                        </Typography>
                    </Box>
                </Box>
            ) : null}

            <Box
                sx={[
                    {
                        display: 'flex',
                        flexDirection: 'column',
                    },
                    classes.checkoutButton,
                ]}
            >
                {allItemsCalculated ? (
                    <Typography variant="small">
                        {t('Proceed to review your order and complete your purchase')}
                    </Typography>
                ) : (
                    <Typography variant="small">{t('Calculate your order to see the subtotal price')}</Typography>
                )}

                {allItemsCalculated ? (
                    <Button
                        color="primary"
                        disabled={
                            isLoadingOrder ||
                            isFetching ||
                            isUpdating ||
                            !orderItems?.length ||
                            issuesCount.errors ||
                            !allItemsCalculated
                        }
                        endIcon={<HugeiconsIcon icon={ArrowRight02Icon} />}
                        variant="contained"
                        fullWidth
                        onClick={() => {
                            navigateToReviewOrder()
                        }}
                    >
                        {t('Review order')}
                    </Button>
                ) : (
                    <TbxTooltip
                        title={
                            (hasUnsetItems && t('You must complete all parts settings to calculate your order')) ||
                            (orderItems?.length === 0 && t('Your order is empty')) ||
                            (isCalculating && t('Calculating your order')) ||
                            ''
                        }
                    >
                        <span>
                            <Button
                                color="primary"
                                disabled={
                                    isLoadingOrder ||
                                    isFetching ||
                                    isCalculating ||
                                    hasUnsetItems ||
                                    orderItems?.length === 0 ||
                                    !isUpdateQueueEmpty
                                }
                                startIcon={
                                    <HugeiconsIcon
                                        icon={CalculateIcon}
                                        size={21}
                                    />
                                }
                                variant="contained"
                                fullWidth
                                onClick={() => {
                                    handleCalculateOrder()
                                }}
                            >
                                {t('Calculate order')}
                            </Button>
                        </span>
                    </TbxTooltip>
                )}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                }}
            >
                {storeSettings?.webStoreSupportEmail || storeSettings?.webStoreContactNumber ? (
                    <Box className="needHelp">
                        <Alert
                            severity="info"
                            variant="standard"
                        >
                            <AlertTitle>{t('Need help with your files?')}</AlertTitle>
                            {storeSettings?.webStoreSupportEmail && !storeSettings?.webStoreContactNumber ? (
                                <>
                                    {t('Contact us at')}{' '}
                                    <Link href={`mailto:${storeSettings?.webStoreSupportEmail}`}>
                                        {storeSettings?.webStoreSupportEmail}
                                    </Link>
                                </>
                            ) : null}
                            {!storeSettings?.webStoreSupportEmail && storeSettings?.webStoreContactNumber ? (
                                <>
                                    {t('Call us at')}{' '}
                                    <Link href={`tel:${storeSettings?.webStoreContactNumber}`}>
                                        {storeSettings?.webStoreContactNumber}
                                    </Link>
                                </>
                            ) : null}
                            {storeSettings?.webStoreSupportEmail && storeSettings?.webStoreContactNumber ? (
                                <>
                                    {t('Contact us at')}{' '}
                                    <Link href={`mailto:${storeSettings?.webStoreSupportEmail}`}>
                                        {storeSettings?.webStoreSupportEmail}
                                    </Link>{' '}
                                    {t('or call us at')}{' '}
                                    <Link href={`tel:${storeSettings?.webStoreContactNumber}`}>
                                        {storeSettings?.webStoreContactNumber}
                                    </Link>
                                </>
                            ) : null}
                        </Alert>
                    </Box>
                ) : null}
            </Box>
        </Box>
    )
}

export default OrderSummary
